import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LangSwitcher } from "../components/LangSwitcher";
import { usePrivateSeo } from "../lib/useSeo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

/** Forced first-login password change. */
export default function ChangePassword() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  usePrivateSeo(t("account.changeTitle"));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError(t("account.tooShort")); return; }
    if (next !== confirm) { setError(t("account.mismatch")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      setUser(data);
      toast.success(t("account.changed"));
      navigate(data.role === "ambassador" ? "/" : "/staff", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="vy-hero px-6 py-6">
        <div className="mx-auto flex max-w-[520px] items-center">
          <span className="font-display text-xl font-bold text-white">
            Vytali<span className="text-accent">Days</span>
          </span>
          <span className="ml-auto"><LangSwitcher dark /></span>
        </div>
      </div>
      <div className="mx-auto max-w-[520px] p-4">
        <form onSubmit={submit} data-testid="change-password-form" className="vy-card p-6">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <h1 className="mt-3 font-display text-2xl font-bold">{t("account.changeTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("account.changeLead")}</p>
          <p className="mt-2 text-sm font-semibold">{user?.email}</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold">{t("account.currentPassword")}</label>
              <Input data-testid="current-password-input" type="password" required value={current}
                autoComplete="current-password"
                onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-12" />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("account.newPassword")}</label>
              <Input data-testid="new-password-input" type="password" required value={next}
                autoComplete="new-password"
                onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-12" />
              <p className="mt-1 text-xs text-muted-foreground">{t("auth.passwordHint")}</p>
            </div>
            <div>
              <label className="text-sm font-semibold">{t("account.confirmPassword")}</label>
              <Input data-testid="confirm-password-input" type="password" required value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12" />
            </div>
          </div>

          {error && (
            <p data-testid="change-password-error"
              className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </p>
          )}
          <Button data-testid="change-password-submit" type="submit" disabled={busy}
            className="mt-6 h-14 w-full rounded-full text-base font-bold">
            {busy ? t("common.loading") : t("account.savePassword")}
          </Button>
          <button type="button" data-testid="change-password-logout" onClick={logout}
            className="mt-4 w-full text-sm font-semibold text-muted-foreground">
            {t("common.logout")}
          </button>
        </form>
      </div>
    </div>
  );
}
